import { createSelector } from 'reselect';
import { type IPermissions } from '../../models/auth';
import { type AppState } from '../../types/store/states/AppState';
import {
  type ApiRolePermissions,
  type ApiRoleSummary,
} from '@reliance/types/models/ApiRole';

const hasPermission = (permissions: ApiRolePermissions, permission: string) =>
  permissions[permission] == true;

const getActiveRolePermissions = (role: ApiRoleSummary): ApiRolePermissions =>
  Object.fromEntries(
    Object.entries(role.permissions).filter(([, allowed]) => allowed !== false),
  );

const getAllPermissions = (state: AppState): IPermissions => {
  const { facilityUser } = state.auth;

  let permissionsList: ApiRolePermissions = {};
  const permissions: IPermissions = {
    hasPermission: (permission: string) =>
      hasPermission(permissionsList, permission),
    hasOneOfPermissions: (permissions: string[]) =>
      permissions.reduce(
        (prev, cur) => prev || hasPermission(permissionsList, cur),
        false,
      ),
    hasAllPermissions: (permissions: string[]) =>
      permissions.reduce(
        (prev, cur) => prev && hasPermission(permissionsList, cur),
        true,
      ),
  };

  if (facilityUser == null) {
    return permissions;
  }

  permissionsList = facilityUser.roles
    .map(getActiveRolePermissions)
    .reduce<ApiRolePermissions>(
      (previous, current) => ({ ...previous, ...current }),
      {},
    );

  return permissions;
};

export const getPermissions = createSelector(
  [getAllPermissions],
  (allPermissions) => allPermissions,
);
